import { type ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { Badge } from '@/components/Badge/Badge';
import { cn } from '@/util/utils';

type CardProps = ComponentProps<'div'> & VariantProps<typeof cardVariants>;

/**
 * ui-card, the surface hierarchy made concrete. Main pages sit on the tinted canvas, so
 * their cards float (a hairline plus shadow/xs). Subpages sit on white, so their cards use a
 * banded header instead. Every card is rounded-md with 20px inner padding.
 */
function Card({ className, floating, ref, ...props }: CardProps) {
    return (
        <div
            data-slot="card"
            ref={ref}
            className={cn(cardVariants({ floating }), className)}
            {...props}
        />
    );
}

const cardVariants = cva(
    'bg-card text-card-foreground border-border flex min-w-0 flex-col rounded-md border',
    {
        variants: {
            floating: {
                true: 'shadow-xs',
                false: '',
            },
        },
        defaultVariants: { floating: false },
    },
);

type CardHeaderProps = ComponentProps<'div'> & {
    /** The subpage treatment: a recessed band with a hairline under it. */
    banded?: boolean;
};

function CardHeader({ className, banded, ref, ...props }: CardHeaderProps) {
    return (
        <div
            data-slot="card-header"
            data-banded={banded || undefined}
            ref={ref}
            className={cn(
                'flex min-h-[68px] flex-wrap items-center gap-2 px-(--layout-card-padding) py-[18px]',
                banded && 'bg-muted border-border min-h-14 rounded-t-md border-b py-4',
                className,
            )}
            {...props}
        />
    );
}

/** The condensed uppercase card title the dashboard draws ("ORDERS", "SALES"). */
function CardTitle({ className, ref, ...props }: ComponentProps<'h2'>) {
    return (
        <h2
            data-slot="card-title"
            ref={ref}
            className={cn('text-label-lg text-foreground', className)}
            {...props}
        />
    );
}

/**
 * ui-card's `count`: the muted note beside the title ("851 of 853", "3 rates · 2 past hidden").
 * It says what the card holds and what a filter is holding back, so the number can always be
 * reconciled with the rows.
 */
function CardCount({ className, ref, ...props }: ComponentProps<'span'>) {
    return (
        <span
            data-slot="card-count"
            ref={ref}
            className={cn('text-muted-foreground text-xs leading-4', className)}
            {...props}
        />
    );
}

/**
 * ui-card's `badge`: the pill beside the title that a register's card carries its count in
 * ("43 ORDERS", "1,204 PRODUCTS"). A different slot from `count`, which is muted text: the
 * prototype draws both, and Orders and Products use this one (ALS-223).
 */
function CardBadge({ className, ...props }: ComponentProps<typeof Badge>) {
    return (
        <Badge
            data-slot="card-badge"
            variant="secondary"
            // 26px with 5px by 10px padding, which is the prototype card badge's measured box.
            size="lg"
            className={className}
            {...props}
        />
    );
}

type CardStatusProps = ComponentProps<'span'> & {
    /**
     * The dot's colour. Neutral says a fact; success, a change that landed; warning, a state to
     * notice; error, a thing that failed, which turns the words destructive too (layout.css).
     */
    tone?: 'neutral' | 'success' | 'warning' | 'error';
};

/**
 * ui-card's `status`: a right-aligned note with a dot ("Filtered · 1 filter", "Saved · just
 * now"), muted, sitting before the header's actions. It reports the card's state rather than
 * offering a control.
 */
function CardStatus({ className, tone = 'neutral', ref, ...props }: CardStatusProps) {
    return (
        <span
            data-slot="card-status"
            ref={ref}
            className={cn(
                'text-muted-foreground ml-auto inline-flex items-center gap-2 text-xs leading-4',
                tone === 'error' && 'text-destructive',
                '[&+[data-slot=card-action]]:ml-4',
                className,
            )}
        >
            <span
                aria-hidden
                className={cn(
                    'size-1.5 shrink-0 rounded-full',
                    tone === 'success' && 'bg-success',
                    tone === 'warning' && 'bg-warning',
                    tone === 'neutral' && 'bg-muted-foreground',
                    tone === 'error' && 'bg-destructive',
                )}
            />
            <span {...props} />
        </span>
    );
}

function CardDescription({ className, ref, ...props }: ComponentProps<'p'>) {
    return (
        <p
            data-slot="card-description"
            ref={ref}
            className={cn('text-muted-foreground text-sm', className)}
            {...props}
        />
    );
}

/**
 * Whatever sits at the far end of the header row: a count, a status note, buttons. It never grows
 * past the header: when its contents are wider than a phone's card (a count and "Write an
 * announcement" at 320), they wrap onto a second line instead of pushing the card off screen.
 */
function CardAction({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-action"
            ref={ref}
            className={cn(
                'ml-auto flex max-w-full shrink-0 flex-wrap items-center gap-2',
                className,
            )}
            {...props}
        />
    );
}

type CardContentProps = ComponentProps<'div'> & {
    /** No inner padding, for a table or list that runs to the card's edges. */
    flush?: boolean;
};

function CardContent({ className, flush, ref, ...props }: CardContentProps) {
    return (
        <div
            data-slot="card-content"
            ref={ref}
            className={cn('min-w-0', !flush && 'p-(--layout-card-padding)', className)}
            {...props}
        />
    );
}

function CardFooter({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-footer"
            ref={ref}
            className={cn(
                'border-border flex items-center gap-2 border-t px-(--layout-card-padding) py-4',
                className,
            )}
            {...props}
        />
    );
}

export {
    Card,
    CardHeader,
    CardFooter,
    CardTitle,
    CardCount,
    CardBadge,
    CardStatus,
    CardAction,
    CardDescription,
    CardContent,
};
